import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * Base HeaderDrawer (main drawer open/close, trap focus, ESC key).
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  #onKeyUp = (event) => {
    if (event.key === 'Escape') {
      this.#close(this.#getDetailsElement(event));
    }
  };

  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;
    return event.target.closest('details') ?? this.refs.details;
  }

  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');
    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      setTimeout(() => trapFocus(details), 0);
    });
  }

  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  close() {
    this.#close(this.refs.details);
  }

  #close(details) {
    const summary = details.querySelector('summary');
    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        this.querySelectorAll('details[open]').forEach(reset);
      } else {
        setTimeout(() => trapFocus(this.refs.details), 0);
      }
    });
  }

  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', (event) => {
        if (event.target instanceof HTMLElement) {
          event.target.style.setProperty('will-change', 'unset');
        }
      });
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}

/**
 * Enhanced HeaderDrawer with submenu support, animations, and backdrop closing.
 */
if (!customElements.get('header-drawer-submenus')) {
  class HeaderDrawerWithSubmenus extends HeaderDrawer {
    connectedCallback() {
      super.connectedCallback();

      this.submenuTriggers = this.querySelectorAll('[data-submenu-trigger]');
      this.submenuPanels = this.querySelectorAll('.menu-drawer__submenu-panel');
      this.backButtons = this.querySelectorAll('.menu-drawer__back-button');

      this._triggerHandler = (e) => {
        e.preventDefault();
        const trigger = e.currentTarget;
        const targetId = trigger.getAttribute('data-submenu-target');
        this.openSubmenu(targetId, trigger.textContent.trim());
      };

      this._backHandler = (e) => {
        const panel = e.currentTarget.closest('.menu-drawer__submenu-panel');
        this.closeSubmenu(panel);
      };

      this.submenuTriggers.forEach((t) => t.addEventListener('click', this._triggerHandler));
      this.backButtons.forEach((b) => b.addEventListener('click', this._backHandler));

      // ✅ Backdrop close support
      this._backdrop = document.querySelector('.menu-drawer__backdrop');
      if (this._backdrop) {
        this._backdropHandler = () => this.close();
        this._backdrop.addEventListener('click', this._backdropHandler);
      }
    }

    disconnectedCallback() {
      if (this.submenuTriggers) this.submenuTriggers.forEach((t) => t.removeEventListener('click', this._triggerHandler));
      if (this.backButtons) this.backButtons.forEach((b) => b.removeEventListener('click', this._backHandler));
      if (this._backdrop && this._backdropHandler) {
        this._backdrop.removeEventListener('click', this._backdropHandler);
      }
      super.disconnectedCallback && super.disconnectedCallback();
    }

    close() {
      super.close();
      if (this.refs?.details) {
        let ran = false;
        onAnimationEnd(this.refs.details, () => {
          if (!ran) {
            ran = true;
            this._hideAllSubmenuPanels();
          }
        });
        setTimeout(() => {
          if (!ran) this._hideAllSubmenuPanels();
        }, 260);
      } else {
        setTimeout(() => this._hideAllSubmenuPanels(), 200);
      }
    }

    _hideAllSubmenuPanels() {
      if (!this.submenuPanels) return;
      this.submenuPanels.forEach((p) => {
        p.classList.remove('submenu-open');
        p.style.display = 'none';
      });
    }

    open(event) {
      super.open(event);
      const mainMenu = this.querySelector('.menu-drawer__menu--root') || this.querySelector('.menu-drawer__navigation');
      if (!mainMenu) return;

      const animateMain = () => {
        const root = mainMenu.querySelector('.menu-drawer__menu') || mainMenu;
        this.animateItems(root);
      };

      if (this.refs?.details) {
        let ran = false;
        onAnimationEnd(this.refs.details, () => {
          if (!ran) {
            ran = true;
            animateMain();
          }
        });
        setTimeout(() => {
          if (!ran) animateMain();
        }, 160);
      } else {
        setTimeout(animateMain, 120);
      }
    }

    animateItems(container) {
      if (!container) return;
      const items = container.querySelectorAll('li, a');
      items.forEach((el, i) => {
        el.style.transition = 'none';
        el.style.opacity = '0';
        el.style.transform = 'translate(-10px, -10px)';
        el.offsetWidth; // reflow
        const delay = `${(i * 0.07).toFixed(2)}s`;
        el.style.transition = `opacity 0.4s ease ${delay}, transform 0.4s ease ${delay}`;
        requestAnimationFrame(() => {
          el.style.opacity = '1';
          el.style.transform = 'translate(0, 0)';
        });
      });
    }

    openSubmenu(targetId, title) {
      const submenu = this.querySelector(`#${targetId}`);
      if (!submenu) return;

      const titleEl = submenu.querySelector('.menu-drawer__submenu-title');
      if (titleEl) titleEl.textContent = title;

      submenu.style.display = 'block';
      requestAnimationFrame(() => {
        submenu.classList.add('submenu-open');
        const itemsRoot = submenu.querySelector('.menu-drawer__menu') || submenu;
        this.animateItems(itemsRoot);
      });
    }

    closeSubmenu(submenu) {
      if (!submenu) return;
      submenu.classList.remove('submenu-open');
      const onEnd = () => {
        submenu.style.display = 'none';
        submenu.removeEventListener('transitionend', onEnd);
      };
      submenu.addEventListener('transitionend', onEnd);
      setTimeout(() => {
        if (submenu.style.display !== 'none') submenu.style.display = 'none';
      }, 260);
    }
  }

  customElements.define('header-drawer-submenus', HeaderDrawerWithSubmenus);
}
